/*
    By Campaign: one row per campaign and Target Action (MPN-1688, restyled for MPN-2045).

    THREE LENSES, not twenty columns at once. Twenty is the shape that has somebody scrolling
    sideways hunting for the one they wanted. Most readings of this report are "did it work"
    (Results); a minority are "should I trust this" (Diagnostics); Full is the export-adjacent
    case. Client, Campaign and Target Action are pinned into every lens, because they are how
    you know which row you are looking at.

    Every heading that has a definition carries it. A number nobody believes is this report's
    most likely failure, and the answer is usually one paragraph long.

    WHAT MPN-2045 CHANGED, and why each one is not cosmetic:

      - The table is a card with the same head, count pill and typography as the Overview
        rollups. It was Bootstrap's `table table-sm` inside a page that styles nothing that
        way, so the tab people spend the most time on was the one that looked least like the
        report.
      - "Showing 12 of 40 rows · 14 columns". The row count was there; the column count was
        not, and switching lens silently changes what Export CSV contains.
      - The filters are behind a toggle, and the toggle carries how many are set. Thirteen
        always-visible input boxes read as part of the data, and the one that was narrowing the
        table was indistinguishable from the twelve that were not.
      - Client and Campaign link out. `launch_id` addresses no page, so Campaign was the one
        identifier here that led nowhere; the server now sends the engagement for it.
*/
import { Download, Search, X } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { Paths } from "@/paths";
import ChipMenu, { toggleValue } from "@/shared/components/ChipMenu/ChipMenu";

import ColumnInfo from "./ColumnInfo";
import { PowerPill, SrmPill, TierPill } from "./StatusPill";
import {
    anyFilterActive, CHIP_FILTERS, columnCount, columnsIn, describeFilters, EMPTY, grainNote,
    groupsFor, interval, optionsFor, pp, pValue, rate, relativeLift, shortDate, shortStamp,
    sizePair, valueLabel, type HoldoutFilters, type HoldoutRow, type Lens,
} from "./holdoutReport";

const LENSES: { key: Lens; label: string }[] = [
    { key: "results", label: "Results" },
    { key: "diagnostics", label: "Diagnostics" },
    { key: "full", label: "Full" },
];

export default function ByCampaignTable({
    rows, visible, filters, setFilters, lens, setLens, onExport, onExclude, canExclude,
}: {
    rows: HoldoutRow[]; visible: HoldoutRow[];
    filters: HoldoutFilters; setFilters: (f: HoldoutFilters) => void;
    lens: Lens; setLens: (l: Lens) => void;
    onExport: () => void; onExclude: (row: HoldoutRow) => void;
    /** Excluding a row writes an audit record and moves every rollup on the page. Read-only
        viewers see the table without the column rather than a button that 403s. */
    canExclude: boolean;
}) {
    const groups = useMemo(() => groupsFor(lens), [lens]);
    const shown = useMemo(() => new Set(groups.map((group) => group.key)), [groups]);
    const active = anyFilterActive(filters);

    const toggle = (key: typeof CHIP_FILTERS[number]["key"]) => (value: string) =>
        setFilters({ ...filters, [key]: toggleValue(filters[key] ?? [], value) });
    const clearChip = (key: typeof CHIP_FILTERS[number]["key"]) => () =>
        setFilters({ ...filters, [key]: [] });

    return (
        <section className="holdout-report__card" id="holdout-by-campaign">
            <div className="holdout-report__section-head">
                <div className="holdout-report__section-title">
                    <h2>Results by Campaign</h2>
                    <span className="holdout-report__count">{visible.length}</span>
                </div>
                <p className="holdout-report__caption">
                    One row per campaign and <strong>Target Action</strong> — the grain the
                    results are measured on, not one row per campaign. {grainNote(rows)}.
                </p>
            </div>

            <div className="holdout-report__toolbar">
                {/* Neutral, not Bootstrap's blue `btn-outline-secondary` group. Three column
                    sets are a view switch; a filled primary button says "this does something"
                    and had people avoiding the lens that answers their question. */}
                <div className="holdout-report__segmented" role="group" aria-label="Columns">
                    {LENSES.map((option) => (
                        <button key={option.key} type="button"
                            className={`holdout-report__segment${
                                lens === option.key ? " holdout-report__segment--on" : ""}`}
                            aria-pressed={lens === option.key}
                            onClick={() => setLens(option.key)}>
                            {option.label}
                        </button>
                    ))}
                </div>

                <span className="holdout-report__toolbar-note">
                    Showing {visible.length} of {rows.length}{" "}
                    {rows.length === 1 ? "row" : "rows"} &middot; {columnCount(lens)} columns
                </span>

                <div className="holdout-report__toolbar-end">
                    <span className="holdout-report__toolbar-note">
                        Exports every column for the rows shown
                    </span>
                    <button type="button" className="holdout-report__chip-button"
                        onClick={onExport} disabled={visible.length === 0}>
                        <Download size={13} aria-hidden="true" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* The platform's chip filters, shared with the Engagements list rather than
                reimplemented (MPN-2045). One search across the three identity columns -- it is
                one question asked three ways, and three boxes made somebody choose which to
                type in first -- then a chip per vocabulary, each opening a checklist. */}
            <div className="holdout-report__filterbar">
                <div className="eng-search">
                    <Search size={13} aria-hidden="true" />
                    <input
                        aria-label="Search client, campaign or Target Action"
                        placeholder="Search client, campaign or Target Action"
                        value={filters.q ?? ""}
                        onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
                    {(filters.q ?? "") && (
                        <button type="button" aria-label="Clear the search"
                            onClick={() => setFilters({ ...filters, q: "" })}>
                            <X size={12} aria-hidden="true" />
                        </button>
                    )}
                </div>

                {CHIP_FILTERS.map((chip) => (
                    <ChipMenu key={chip.key}
                        label={chip.label}
                        options={optionsFor(rows, chip.field)
                            .map((value) => ({ value, label: valueLabel(value) }))}
                        selected={filters[chip.key] ?? []}
                        onToggle={toggle(chip.key)}
                        onClear={clearChip(chip.key)} />
                ))}

                {/* Only once there is something to clear, as the prototype has it. A dead
                    Clear sitting there permanently reads as a filter that is already on. */}
                {active && (
                    <button type="button" className="holdout-report__filterbar-clear"
                        onClick={() => setFilters({})}>Clear Filters</button>
                )}
            </div>

            {/* Width floors and a sticky head only on Full. Full is the lens that scrolls in
                both directions: its columns were resizing under the cursor on every filter
                change, and a head that scrolls away over eighteen columns of numbers leaves
                you counting across to work out which one you are reading. The narrower lenses
                size to their content, which reads better. */}
            <div className={`holdout-report__table-wrap${
                lens === "full" ? " holdout-report__table-wrap--tall" : ""}`}>
                <table className={`holdout-report__table${
                    lens === "full" ? " holdout-report__table--wide" : ""}`}>
                    <thead>
                        <tr>
                            <th className="is-sticky-head">
                                Client<ColumnInfo column="client" />
                            </th>
                            <th className="is-sticky-head">
                                Campaign<ColumnInfo column="campaign" />
                            </th>
                            <th className="is-sticky-head">
                                Target Action<ColumnInfo column="target_action" />
                            </th>
                            {groups.map((group) => (
                                <th key={group.key}
                                    className={`is-sticky-head${
                                        group.align === "right" ? " is-num" : ""}`}
                                    colSpan={columnsIn(group)}>
                                    {group.label}<ColumnInfo column={group.key} />
                                    {group.parts && (
                                        <div className="holdout-report__subhead">
                                            {group.parts.map((part, index) => (
                                                <span key={part}>
                                                    {index > 0 && " / "}
                                                    {part}
                                                    {group.partColumns && (
                                                        <ColumnInfo column={group.partColumns[index]} />
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </th>
                            ))}
                            {/* The column says whose job this is. An unlabelled column of
                                red "Exclude" links read as something anybody passing through
                                might click (Holly, QA, 7 Oct 2026). */}
                            {canExclude && (
                                <th className="is-sticky-head">
                                    Data Analyst
                                    <span className="holdout-report__subhead">Report only</span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={`${row.launch_id}-${row.journey_step_id}`}>
                                <td className="is-name">
                                    {row.client_id ? (
                                        <Link to={`../${Paths.ORGANIZATION_DETAIL}/${row.client_id}`}>
                                            {row.client}
                                        </Link>
                                    ) : row.client}
                                </td>
                                <td className="is-name">
                                    {row.engagement_id ? (
                                        <Link to={`../${Paths.ENGAGEMENT}/${row.engagement_id}`}>
                                            {row.campaign}
                                        </Link>
                                    ) : row.campaign}
                                </td>
                                <td>
                                    {row.target_action}
                                    {/* The dimension this row IS, since MPN-2076 item 4 gave
                                        each one its own row. It used to read "4 dimensions"
                                        because they were summed into the step; now a step with
                                        four terms is four rows and each names itself. Blank on
                                        the steps that have no dimensions, which is most. */}
                                    {row.dimension_name && (
                                        <span className="holdout-report__dimensions">
                                            {row.dimension_name}
                                        </span>
                                    )}
                                </td>
                                {shown.has("campaign_status") && (
                                    <td><StatusBadge status={row.campaign_status} /></td>
                                )}
                                {shown.has("use_case") && (
                                    <>
                                        <td>{row.use_case_label || row.use_case
                                            || <span className="is-empty">{EMPTY}</span>}</td>
                                        <td>{row.funnel_stage_label}</td>
                                    </>
                                )}
                                {shown.has("days_running") && (
                                    <td className="is-num">{row.days_running ?? EMPTY}</td>
                                )}
                                {/* One cell, "2,397 / 598". Two columns under one Size heading
                                    with identical sub-labels read as one number per row until
                                    you notice the pair -- and the pair is the whole point for
                                    anybody checking an SRM flag. */}
                                {shown.has("size") && (
                                    <td className="is-num">
                                        {sizePair(row.treatment_size, row.control_size)}
                                    </td>
                                )}
                                {shown.has("conversions") && (
                                    <td className="is-num">
                                        {sizePair(row.treatment_conversions,
                                                  row.control_conversions)}
                                    </td>
                                )}
                                {shown.has("conversion_rate") && (
                                    <>
                                        <td className="is-num">{rate(row.treatment_rate)}</td>
                                        <td className="is-num">{rate(row.control_rate)}</td>
                                    </>
                                )}
                                {/* The interval under the estimate rather than beside it. Side
                                    by side, two numbers of the same size in adjacent columns
                                    read as two findings; stacked, the interval reads as what it
                                    is -- the precision of the one above it. */}
                                {shown.has("absolute_lift") && (
                                    <td className="is-num">
                                        <span className="holdout-report__stacked">
                                            <span>{pp(row.absolute_lift)}</span>
                                            <span className="is-bound">
                                                {interval(row.absolute_lift_lower,
                                                          row.absolute_lift_upper, "pp")}
                                            </span>
                                        </span>
                                    </td>
                                )}
                                {shown.has("relative_lift") && (
                                    <td className="is-num">
                                        <span className="holdout-report__stacked">
                                            <span className={row.absolute_lift != null
                                                && row.absolute_lift > 0
                                                ? "holdout-report__lift--up" : undefined}>
                                                {relativeLift(row.relative_lift, row.control_rate)}
                                            </span>
                                            <span className="is-bound">
                                                {row.control_rate === 0 ? EMPTY : interval(
                                                    row.relative_lift_lower,
                                                    row.relative_lift_upper, "%")}
                                            </span>
                                        </span>
                                    </td>
                                )}
                                {shown.has("tier") && <td><TierPill tier={row.tier} /></td>}
                                {shown.has("p_value") && (
                                    <td className="is-num">{pValue(row.p_value)}</td>
                                )}
                                {shown.has("date_reached") && (
                                    <td className="is-num">
                                        {row.date_reached
                                            ? shortDate(row.date_reached)
                                            : <span className="is-empty">{EMPTY}</span>}
                                    </td>
                                )}
                                {shown.has("srm_check") && (
                                    <td><SrmPill check={row.srm_check} /></td>
                                )}
                                {shown.has("statistical_power") && (
                                    <td><PowerPill power={row.statistical_power} /></td>
                                )}
                                {shown.has("last_calculated") && (
                                    <td className="is-num is-bound">
                                        {shortStamp(row.last_calculated)}
                                    </td>
                                )}
                                {canExclude && (
                                    <td>
                                        <button type="button"
                                            className="holdout-report__row-action"
                                            onClick={() => onExclude(row)}>Exclude</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={40} className="is-empty">
                                    {active ? (
                                        <>
                                            Filters combine with AND, and no row has{" "}
                                            {describeFilters(filters)}. On this report that is
                                            often the answer rather than a mistake.
                                        </>
                                    ) : (
                                        "No campaigns are in holdout testing."
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

/**
 * Campaign status as a badge.
 *
 * It arrived as the raw stored value in a column of sentence-case text: "LIVE" beside
 * "Register" reads as shouting rather than as a state. A badge makes it a state, and Ended gets
 * a quieter tone than Live because a result from a finished campaign is final rather than
 * provisional.
 */
function StatusBadge({ status }: { status: string }) {
    if (!status) return <span className="is-empty">{EMPTY}</span>;
    const key = status.toLowerCase();
    const tone = key === "live" ? "live" : key === "ended" ? "ended" : "other";
    return (
        <span className={`holdout-report__status holdout-report__status--${tone}`}>
            {valueLabel(status)}
        </span>
    );
}
